import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { DismissButton } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import type { DOMRef } from '@react-types/shared';
import classNames from 'classnames';
import React, { forwardRef, useContext, useRef } from 'react';
import type { DialogProps } from '../../../interface-and-type';
import { useDOMRef } from '../../../utils';
import { ActionButton } from '../../date-picker-button/action-button';
import { Grid } from '../../date-picker-layout/grid';
import type { DialogContextValue } from '../dialog-trigger/controls/dialog-context';
import { DialogContext } from '../dialog-trigger/controls/dialog-context';

let sizeMap = {
  S: 'small',
  M: 'medium',
  L: 'large',
  fullscreen: 'fullscreen',
  fullscreenTakeover: 'fullscreenTakeover'
};

const Dialog = (props: DialogProps, ref: DOMRef) => {
  let { type = 'modal', ...contextProps } = useContext(DialogContext) || ({} as DialogContextValue);
  let {
    children,
    isDismissable = contextProps.isDismissable,
    onDismiss = contextProps.onClose,
    size
  } = props;

  size = type === 'popover' ? size || 'S' : size || 'L';

  let domRef = useDOMRef(ref);
  let gridRef = useRef(null);
  let sizeVariant = sizeMap[type] || sizeMap[size];
  let { dialogProps } = useDialog(mergeProps(contextProps, props), domRef);

  let dismissButton: JSX.Element;
  if (type === 'popover' || type === 'tray') {
    dismissButton = <DismissButton onDismiss={onDismiss} />;
  }

  return (
    <FocusScope contain restoreFocus>
      <section
        {...dialogProps}
        style={props.UNSAFE_style}
        className={classNames(
          'dialog-style',
          {
            [`dialog-size-${sizeVariant}`]: sizeVariant,
            'dialog-dismissable-class': isDismissable
          },
          props.UNSAFE_className
        )}
        ref={domRef}
      >
        <Grid ref={gridRef} UNSAFE_className={'dialog-grid-style'}>
          {children}
          {isDismissable && (
            <ActionButton UNSAFE_className={'dialog-closeButton-class'} isQuiet onPress={onDismiss}>
              <FontAwesomeIcon icon={faTimes} />
            </ActionButton>
          )}
        </Grid>
        {dismissButton}
      </section>
    </FocusScope>
  );
};

let _Dialog = forwardRef(Dialog);
export { _Dialog as Dialog };
